import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { FrameOption } from '@renderer/types/configState'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'

const Frame: React.FC = () => {
  const navigate = useNavigate()
  const { projectId } = useParams()
  const { setSessionState, logActivity, userPreferences } = useSessionStateStore()
  const { frames: allFrames, frameCategories } = useConfigStore()

  const [currentFrames, setCurrentFrames] = useState<FrameOption[]>([])
  const [selectedFrame, setSelectedFrame] = useState<string | null>(null)

  // Get the booth mode from user preferences
  const boothMode = userPreferences.boothMode

  useEffect(() => {
    // Filter frames based on booth mode
    let framesToShow: FrameOption[] = []

    if (frameCategories && frameCategories.length > 0) {
      // Find category based on booth mode
      const category = frameCategories.find((cat) => {
        const categoryName = cat.name.toLowerCase()
        return categoryName.includes(boothMode || '')
      })

      framesToShow = category?.frames || allFrames
    } else {
      framesToShow = allFrames
    }

    setCurrentFrames(framesToShow)

    // Auto-select first frame and set it in session state
    if (framesToShow.length > 0) {
      const firstFrame = framesToShow[0]
      setSelectedFrame(firstFrame._id)

      // Set the frame in session state
      const photoImage = firstFrame.drawParams.images.find((img) => img.type.startsWith('photo-'))
      const photoRatio = photoImage ? photoImage.width / photoImage.height : null

      setSessionState({
        userPreferences: {
          frameId: firstFrame._id,
          framePhotoRatio: photoRatio
        }
      })

      logActivity(`Auto-selected frame: ${firstFrame.name}`)
    } else {
      // If no frames found for the booth mode, log a warning
      logActivity(`No frames found for booth mode: ${boothMode}`)
    }
  }, [boothMode, frameCategories, allFrames, setSessionState, logActivity])

  const handleFrameSelect = (frameId: string): void => {
    setSelectedFrame(frameId)

    const frame = allFrames.find((f) => f._id === frameId)
    const photoImage = frame?.drawParams.images.find((img) => img.type.startsWith('photo-'))
    const photoRatio = photoImage ? photoImage.width / photoImage.height : null

    setSessionState({
      userPreferences: {
        frameId: frameId,
        framePhotoRatio: photoRatio
      }
    })

    logActivity(`Frame selected: ${frame?.name || frameId}`)
  }

  const handleCancel = (): void => {
    logActivity('Frame selection cancelled, returning to welcome')
    navigate(`/${projectId}`)
  }

  const handleChoose = (): void => {
    if (!selectedFrame) return

    logActivity(`Frame selection confirmed: ${selectedFrame}`)

    // Navigate to checkout for booth-daughter flow
    navigate(`/${projectId}/checkout`)
  }

  const cachedFramesBg = useCachedBackgroundImage('/assets/boothndaughter/frames.webp')
  const cachedCancelBg = useCachedBackgroundImage('/assets/boothndaughter/cancel.webp')
  const cachedChooseBg = useCachedBackgroundImage('/assets/boothndaughter/choose.webp')

  return (
    <div
      className="w-full h-full relative overflow-hidden"
      style={{
        backgroundImage: `url(${cachedFramesBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Cancel Button - Top Left */}
      <div className="absolute top-8 left-8">
        <button
          onClick={handleCancel}
          title="Cancel frame selection"
          style={{
            width: '179px',
            height: '107px',
            backgroundImage: `url(${cachedCancelBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            border: 'none',
            cursor: 'pointer'
          }}
          className="hover:scale-105 transition-transform"
        />
      </div>

      {/* Choose Button - Top Right */}
      <div className="absolute top-8 right-8">
        <button
          onClick={handleChoose}
          disabled={!selectedFrame}
          title="Choose selected frame"
          style={{
            width: '179px',
            height: '107px',
            backgroundImage: `url(${cachedChooseBg})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            border: 'none',
            cursor: selectedFrame ? 'pointer' : 'not-allowed',
            opacity: selectedFrame ? 1 : 0.5
          }}
          className="hover:scale-105 transition-transform disabled:hover:scale-100"
        />
      </div>

      <div className="flex h-full">
        {/* Left Side - Frame List */}
        <div className="w-2/3 flex flex-col p-8 mt-64 px-40 ml-12 mb-16">
          <div className="flex-1 overflow-y-auto overflow-x-hidden">
            <div className="grid grid-cols-3 gap-6">
              {currentFrames.map((frame) => (
                <button
                  key={frame._id}
                  onClick={() => handleFrameSelect(frame._id)}
                  className="cursor-pointer transition-all flex flex-col p-2 items-center rounded-lg hover:scale-105"
                  style={{
                    transform: selectedFrame === frame._id ? 'scale(1.01)' : 'scale(1)'
                  }}
                >
                  <img
                    crossOrigin="anonymous"
                    src={frame.thumbnail}
                    alt={frame.name}
                    className="w-full shadow-lg object-contain rounded"
                    style={{
                      border:
                        selectedFrame === frame._id ? '3px solid #3B82F6' : '2px solid #E5E7EB',
                      height: '400px'
                    }}
                  />
                  <p
                    style={{
                      fontFamily: 'monospace',
                      fontSize: '24px',
                      fontWeight: 'bold',
                      marginTop: '8px',
                      color: selectedFrame === frame._id ? '#3B82F6' : '#374151'
                    }}
                  >
                    {frame.name}
                  </p>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Selected Frame Preview */}
        <div className="w-1/3 flex items-center justify-center p-8 mr-12 mt-24">
          {selectedFrame && (
            <div className="w-full max-w-sm">
              <div className="p-4">
                <img
                  src={allFrames.find((f) => f._id === selectedFrame)?.thumbnail || ''}
                  alt="Selected Frame Preview"
                  className="w-full object-contain rounded shadow-xl"
                  crossOrigin="anonymous"
                  style={{ height: '500px' }}
                />
              </div>
              <p
                style={{
                  fontFamily: 'monospace',
                  fontSize: '32px',
                  fontWeight: 'bold',
                  marginTop: '12px',
                  color: 'white',
                  textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                  textAlign: 'center'
                }}
              >
                {allFrames.find((f) => f._id === selectedFrame)?.name || ''}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default Frame
